import { makeAutoObservable } from 'mobx'
import { type INormalizedApiError } from 'shared/api'
import { AgentActionSendMessage } from 'entities/AgentActions'
import { type RequestAttachment } from 'entities/Attachment/model/RequestAttachment'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { AgentActionMessageStore } from 'widgets/AgentActions'
import { SurveyMessageActionsStore } from 'pages/surveys/ui/SurveyView/store/surveyMessageActionsStore'
import {
  collectSurveyAttachmentsMissingFromBody,
  resolveSurveyBodyAttachments,
  restoreSurveyMediaAttachments,
} from 'pages/surveys/lib/reconstructSurveyAttachments'

export type ICategoryMessageValue = {
  message: string
  media_url: RequestAttachment[]
}

export class CategoryMessageStore {
  isAdded = false
  error = ''
  readonly model: AgentActionSendMessage
  readonly messageStore: AgentActionMessageStore
  private _messageActions: SurveyMessageActionsStore | null = null
  private _restoreToken: symbol | null = null

  constructor(
    private _rootKey: string,
    private _defaultMessage: string,
    private _entities: IEntitiesStore
  ) {
    this.model = AgentActionSendMessage.Create()
    this.messageStore = new AgentActionMessageStore(this.model, _entities, {
      makeActions: ({ messageFieldStore, savedRepliesStore }) => {
        this._messageActions = new SurveyMessageActionsStore(
          messageFieldStore,
          savedRepliesStore,
          _entities
        )
        return this._messageActions.actions
      },
    })
    makeAutoObservable(this)
  }

  get hasMessage() {
    return this.isAdded && (Boolean(this.model.message.trim()) || this.model.attachments.length > 0)
  }

  get isModified() {
    if (!this.isAdded) return false
    return (
      this.model.message.trim() !== this._defaultMessage.trim() || this.model.attachments.length > 0
    )
  }

  get previewHtml() {
    return this.model.message ? this.messageStore.message.messageHtml : ''
  }

  get previewAttachments() {
    return this.messageStore.message.attachments
  }

  get paramsValue(): ICategoryMessageValue | null {
    if (!this.hasMessage) return null
    const message = this.model.message
    return {
      message,
      media_url: [
        ...this.messageStore.message.media_url,
        ...collectSurveyAttachmentsMissingFromBody(message, this.messageStore.message.attachments),
      ],
    }
  }

  init = (value: ICategoryMessageValue | null | undefined) => {
    const message = value?.message ?? ''
    const mediaUrl = value?.media_url ?? []
    this.model.sync(message, restoreSurveyMediaAttachments(mediaUrl))
    this.messageStore.syncMessage()
    this.isAdded = Boolean(message) || mediaUrl.length > 0
    this.error = ''
    this._restoreToken = Symbol('restoreBodyAttachments')
    void this._restoreBodyAttachments(message, mediaUrl, this._restoreToken)
  }

  private _restoreBodyAttachments = async (
    message: string,
    mediaUrl: RequestAttachment[],
    restoreToken: symbol
  ) => {
    const attachments = await resolveSurveyBodyAttachments(this._entities, message, mediaUrl)
    if (restoreToken !== this._restoreToken || !attachments.length) return
    this.messageStore.message.addAttachments(attachments)
  }

  add = () => {
    this.isAdded = true
    this._writeToField(this._defaultMessage)
  }

  remove = () => {
    this.isAdded = false
    this._writeToField('')
    this.setError('')
  }

  revertToDefault = () => {
    this._writeToField(this._defaultMessage)
    this.setError('')
  }

  setError = (value: string) => {
    this.error = value
  }

  validate = (emptyMessageError: string) => {
    if (this.isAdded && this.model.attachments.length > 0 && !this.model.message.trim()) {
      this.setError(emptyMessageError)
      return false
    }
    this.setError('')
    return true
  }

  syncApiErrors = (apiErrors: INormalizedApiError | null | undefined) => {
    const match = apiErrors?.entries().find(([key]) => key.includes(this._rootKey))
    this.setError(match?.[1]?.value ?? '')
  }

  dispose = () => {
    this._restoreToken = null
    this._messageActions?.dispose()
    this.messageStore.dispose()
  }

  private _writeToField = (message: string) => {
    this._restoreToken = null
    this.model.sync(message, [])
    this.messageStore.syncMessage()
  }
}
